import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { UploadCloud, Trash2, Download, LogOut, Loader2, Link2 } from "lucide-react";

function formatSize(bytes) {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} МБ` : `${(bytes / 1024).toFixed(0)} КБ`;
}

function formatDate(iso) {
  return new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function PlatformAdminDashboard() {
  const [admin, setAdmin] = useState(null);
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [versionName, setVersionName] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloadsEnabled, setDownloadsEnabled] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [linkVersionName, setLinkVersionName] = useState("");
  const [externalUrl, setExternalUrl] = useState("");
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const loadReleases = () => api.get("/releases").then(({ data }) => setReleases(data)).catch(() => {});

  useEffect(() => {
    api
      .get("/platform-admin/me")
      .then(({ data }) => {
        setAdmin(data.admin);
        return Promise.all([
          loadReleases(),
          api.get("/releases/download-toggle").then(({ data: t }) => setDownloadsEnabled(t.enabled)),
        ]);
      })
      .catch(() => navigate("/login"))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleToggleDownloads = async (checked) => {
    setToggling(true);
    try {
      await api.put("/releases/download-toggle", { enabled: checked });
      setDownloadsEnabled(checked);
      toast.success(checked ? "Кнопка «Скачать» включена на сайте" : "Кнопка «Скачать» скрыта с сайта");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось изменить настройку");
    } finally {
      setToggling(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Выберите файл для загрузки");
      return;
    }
    if (!versionName.trim()) {
      toast.error("Укажите название версии");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("version_name", versionName.trim());
      await api.post("/releases", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Версия загружена");
      setVersionName("");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      loadReleases();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось загрузить файл");
    } finally {
      setUploading(false);
    }
  };

  const handleAddLink = async (e) => {
    e.preventDefault();
    if (!linkVersionName.trim()) {
      toast.error("Укажите название версии");
      return;
    }
    if (!externalUrl.trim()) {
      toast.error("Вставьте ссылку на файл");
      return;
    }
    setLinkSubmitting(true);
    try {
      await api.post("/releases/link", {
        version_name: linkVersionName.trim(),
        external_url: externalUrl.trim(),
      });
      toast.success("Версия добавлена по ссылке");
      setLinkVersionName("");
      setExternalUrl("");
      loadReleases();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось добавить ссылку");
    } finally {
      setLinkSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/releases/${id}`);
      toast.success("Версия удалена");
      setReleases((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось удалить версию");
    }
  };

  const handleLogout = async () => {
    await api.post("/platform-admin/logout").catch(() => {});
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center gap-2 text-sm text-slate-500" data-testid="platform-admin-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Загрузка…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-6 sm:p-10" data-testid="platform-admin-dashboard">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Управление версиями приложения</h1>
            <p className="text-sm text-slate-500 mt-0.5">{admin?.email}</p>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="platform-admin-logout-button">
            <LogOut className="w-4 h-4 mr-1.5" /> Выйти
          </Button>
        </div>

        <div
          className="bg-white rounded-2xl border border-slate-200 p-6 mb-8 flex flex-wrap items-center justify-between gap-4"
          data-testid="platform-admin-download-toggle-card"
        >
          <div>
            <div className="text-sm font-semibold text-slate-900">Кнопка «Скачать» на сайте</div>
            <p className="text-xs text-slate-500 mt-0.5">
              Можно временно скрыть кнопку на лендинге, не удаляя загруженные файлы, и включить обратно в любой момент.
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="text-sm text-slate-600">{downloadsEnabled ? "Включена" : "Скрыта"}</span>
            <Switch
              checked={downloadsEnabled}
              onCheckedChange={handleToggleDownloads}
              disabled={toggling}
              data-testid="platform-admin-download-toggle-switch"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8" data-testid="platform-admin-add-release-card">
          <Tabs defaultValue="link" data-testid="platform-admin-add-release-tabs">
            <TabsList className="mb-4">
              <TabsTrigger value="link" data-testid="platform-admin-tab-link">Ссылка на файл</TabsTrigger>
              <TabsTrigger value="upload" data-testid="platform-admin-tab-upload">Загрузить файл</TabsTrigger>
            </TabsList>

            <TabsContent value="link">
              <p className="text-xs text-slate-500 mb-4">
                Загрузите файл в облако (Яндекс.Диск, Dropbox) или прикрепите его как файл к GitHub Release
                в вашем репозитории, затем вставьте прямую ссылку на файл сюда — она будет использоваться
                для скачивания версии клиентами.
              </p>
              <form onSubmit={handleAddLink} className="grid sm:grid-cols-[1fr_2fr_auto] gap-4 items-end" data-testid="platform-admin-link-form">
                <div className="space-y-1.5">
                  <Label htmlFor="link-version-name">Название версии</Label>
                  <Input
                    id="link-version-name"
                    placeholder="Например: 2.4.1"
                    value={linkVersionName}
                    onChange={(e) => setLinkVersionName(e.target.value)}
                    data-testid="platform-admin-link-version-name-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="external-url">Ссылка на файл</Label>
                  <Input
                    id="external-url"
                    placeholder="https://..."
                    value={externalUrl}
                    onChange={(e) => setExternalUrl(e.target.value)}
                    data-testid="platform-admin-external-url-input"
                  />
                </div>
                <Button type="submit" disabled={linkSubmitting} className="bg-gradient-to-r from-[#4E9BEE] to-[#2563EB] text-white" data-testid="platform-admin-link-submit-button">
                  {linkSubmitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Link2 className="w-4 h-4 mr-1.5" />}
                  Добавить
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="upload">
              <p className="text-xs text-amber-600 mb-4">
                Прямая загрузка файла сейчас может не работать, если файловое хранилище недоступно — в этом случае используйте вкладку «Ссылка на файл».
              </p>
              <form
                onSubmit={handleUpload}
                className="grid sm:grid-cols-[1fr_1fr_auto] gap-4 items-end"
                data-testid="platform-admin-upload-form"
              >
                <div className="space-y-1.5">
                  <Label htmlFor="version-name">Название версии</Label>
                  <Input
                    id="version-name"
                    placeholder="Например: 2.4.1"
                    value={versionName}
                    onChange={(e) => setVersionName(e.target.value)}
                    data-testid="platform-admin-version-name-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="release-file">Файл</Label>
                  <Input
                    id="release-file"
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    data-testid="platform-admin-file-input"
                  />
                </div>
                <Button type="submit" disabled={uploading} className="bg-gradient-to-r from-[#4E9BEE] to-[#2563EB] text-white" data-testid="platform-admin-upload-button">
                  {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-1.5" />}
                  Загрузить
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="platform-admin-releases-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Версия</TableHead>
                <TableHead>Файл / Ссылка</TableHead>
                <TableHead>Размер</TableHead>
                <TableHead>Загружен</TableHead>
                <TableHead className="text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {releases.map((r) => (
                <TableRow key={r.id} data-testid={`platform-admin-release-row-${r.id}`}>
                  <TableCell className="font-medium text-slate-900">{r.version_name}</TableCell>
                  <TableCell className="text-slate-500 truncate max-w-[220px]">
                    {r.external_url ? (
                      <span className="inline-flex items-center gap-1.5 text-[#2563EB]">
                        <Link2 className="w-3.5 h-3.5 shrink-0" /> {r.external_url}
                      </span>
                    ) : (
                      r.original_filename
                    )}
                  </TableCell>
                  <TableCell className="text-slate-500">{formatSize(r.size)}</TableCell>
                  <TableCell className="text-slate-500">{formatDate(r.created_at)}</TableCell>
                  <TableCell className="text-right space-x-2 whitespace-nowrap">
                    <a href={`${API}/releases/${r.id}/download`} data-testid={`platform-admin-download-${r.id}`}>
                      <Button variant="ghost" size="sm">
                        <Download className="w-4 h-4 mr-1" /> Скачать
                      </Button>
                    </a>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(r.id)} data-testid={`platform-admin-delete-${r.id}`}>
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {releases.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-slate-400 py-8" data-testid="platform-admin-empty-state">
                    Версии ещё не добавлены
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
